import * as React from "react";
import type { SelectOption } from "./types";

interface UseMultiSelectStateOptions {
  value: string[];
  options: SelectOption[];
}

/**
 * Hook to manage multi-select state including selected options cache
 */
export function useMultiSelectState({ value, options }: UseMultiSelectStateOptions) {
  // Cache of selected options so they persist visually across search/result swaps
  const selectedCacheRef = React.useRef<Map<string, SelectOption>>(new Map());

  // Keep cache in sync when options contain any of the selected values
  React.useEffect(() => {
    if (!value || value.length === 0) return;
    const cache = selectedCacheRef.current;
    for (const opt of options) {
      if (value.includes(opt.value)) {
        cache.set(opt.value, opt);
      }
    }
  }, [options, value]);

  // Populate cache from initial relation data if available
  React.useEffect(() => {
    if (!value || value.length === 0) return;
    const cache = selectedCacheRef.current;

    // Check if any value IDs are not yet in cache and not in options
    value.forEach((id) => {
      if (!cache.has(id) && !options.find((o) => o.value === id)) {
        cache.set(id, { value: id, label: id });
      }
    });
  }, [value, options]);

  const selectedOptions = React.useMemo(() => {
    const cache = selectedCacheRef.current;
    // Return in the same order as value[]
    return value.map((id) => {
      const inOptions = options.find((o) => o.value === id);
      if (inOptions) return inOptions;
      const cached = cache.get(id);
      if (cached) return cached;
      // Fallback when we have only the id: render id as label
      return { value: id, label: id } as SelectOption;
    });
  }, [options, value]);

  return { selectedOptions, selectedCacheRef };
}
